import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {LoggedInCallback} from "../../service-auth/cognito.service";
import {Router} from "@angular/router";
import {RoleChangeListener, StartupService} from "../../service-ui/startup.service";
import {MatSidenav} from "@angular/material/sidenav";
import {MediaMatcher} from "@angular/cdk/layout";
import {MatTableDataSource} from '@angular/material/table';
import {BehaviorSubject, combineLatest, Observable, Subject} from 'rxjs';
import {map, takeUntil} from 'rxjs/operators';
import {OrderDeliverToService} from "../../service-data/order-deliver-to.service";
import {OrderDeliverTo} from "../../../model/OrderDeliverTo";
import {ProductService} from "../../service-data/product.service";
import {PrintNodeOrderService} from "../../service-data/print-node-order.service";
import {PrintNodeOrder} from "../../../model/PrintNodeOrder";

@Component({
  selector: 'app-shipment-orders',
  templateUrl: './shipment-orders.component.html',
  styleUrls: ['./shipment-orders.component.css']
})
export class ShipmentOrdersComponent
  implements OnDestroy, OnInit, LoggedInCallback, AfterViewInit, RoleChangeListener {

  /**
   * Lower bound for a "real" ship/deliver timestamp. Rows carrying an
   * epoch (`new Date(0)`) or a year-0001 sentinel value fall below
   * this threshold and are treated as "not shipped / not delivered".
   * 2010 is comfortably before the earliest real Snowfall Press
   * shipment, so anything before it is by definition sentinel data.
   */
  private static readonly MIN_REAL_SHIP_MS = Date.UTC(2010, 0, 1);

  @ViewChild('mainSideNav', {static: true}) mainSideNav: MatSidenav;

  mobileQuery: MediaQueryList;
  private _mobileQueryListener: () => void;

  showSymbolMeanings: boolean = true;

  // Full unfiltered list
  orderDeliverTos: OrderDeliverTo[] = [];

  // Displayed data source & columns
  dataSource: MatTableDataSource<OrderDeliverTo> = new MatTableDataSource<OrderDeliverTo>();
  displayedColumns: string[] = [
    'ID', 'customerOrderId', 'created', 'customer', 'items',
    'carrier', 'tracking', 'status', 'actions'
  ];

  // Row expansion (eye button toggles a single expanded row)
  expandedElement: OrderDeliverTo | null = null;

  // Toolbar filter + search state (BehaviorSubjects like EcommerceOrdersComponent)
  selectedFilters: BehaviorSubject<string[]> = new BehaviorSubject<string[]>(['ALL']);
  searchQuery: BehaviorSubject<string> = new BehaviorSubject<string>('');
  private ordersSubject: BehaviorSubject<OrderDeliverTo[]> = new BehaviorSubject<OrderDeliverTo[]>([]);
  filteredOrders$: Observable<OrderDeliverTo[]>;

  onDestroySubject: Subject<boolean> = new Subject();
  private ngUnsubscribe = new Subject<void>();

  constructor(
    public router: Router,
    public startupService: StartupService,
    public orderDeliverToService: OrderDeliverToService,
    public productService: ProductService,
    public printNodeOrderService: PrintNodeOrderService,
    public changeDetectorRef: ChangeDetectorRef,
    public media: MediaMatcher
  ) {
    this.startupService.addRoleChangeListener(this);
    this.mobileQuery = media.matchMedia('(max-width: 1024px)');
    this._mobileQueryListener = () => changeDetectorRef.detectChanges();
    this.mobileQuery.addListener(this._mobileQueryListener);
  }

  ngOnInit() {
    this.startupService.isAuthenticated(this);

    this.filteredOrders$ = combineLatest([
      this.ordersSubject,
      this.selectedFilters,
      this.searchQuery
    ]).pipe(
      map(([orders, filters, query]) => {
        let filtered = orders || [];

        if (!filters.includes('ALL')) {
          filtered = filtered.filter(o => {
            const cat = this.getShipmentCategory(o);
            for (const f of filters) {
              if (f === cat) { return true; }
              // ADDRESS_ISSUE is a modifier that can co-exist with any category
              if (f === 'ADDRESS_ISSUE' && this.hasAddressIssue(o)) { return true; }
            }
            return false;
          });
        }

        if (query) {
          const q = query.toLowerCase();
          filtered = filtered.filter(o =>
            ('' + o.ID).includes(q) ||
            (o.order && ('' + o.order.customerorderid).toLowerCase().includes(q)) ||
            (o.firstname || '').toLowerCase().includes(q) ||
            (o.lastname || '').toLowerCase().includes(q) ||
            (o.company || '').toLowerCase().includes(q) ||
            (o.address1 || '').toLowerCase().includes(q) ||
            (o.address2 || '').toLowerCase().includes(q) ||
            (o.city || '').toLowerCase().includes(q) ||
            (o.state || '').toLowerCase().includes(q) ||
            (o.postalcode || '').toLowerCase().includes(q) ||
            (o.country || '').toLowerCase().includes(q) ||
            (o.carrier || '').toLowerCase().includes(q) ||
            (o.servicelevel || '').toLowerCase().includes(q) ||
            (o.carriertrackingid || '').toLowerCase().includes(q) ||
            (o.carrierstatus || '').toLowerCase().includes(q) ||
            (o.orderItems || []).some(it =>
              (it.product && it.product.title ? it.product.title.toLowerCase().includes(q) : false) ||
              ('' + (it.product_ID || '')).includes(q)
            )
          );
        }
        return filtered;
      })
    );

    this.filteredOrders$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(orders => {
        this.dataSource.data = orders;
      });
  }

  ngAfterViewInit(): void {
    // nothing extra
  }

  roleChanged(role: string, roleId: string): void {
    this.expandedElement = null;
    this.getData();
  }

  isLoggedIn(message: string, isLoggedIn: boolean) {
    if (!isLoggedIn) {
      this.router.navigate(['/home/login']);
    } else {
      this.getData();
    }
  }

  getData(): void {
    this.orderDeliverToService.getOrderDeliverTos().subscribe(orderDeliverTos => {
      this.orderDeliverTos = orderDeliverTos || [];
      this.ordersSubject.next(this.orderDeliverTos);
    });
  }

  ngOnDestroy(): void {
    this.mobileQuery.removeListener(this._mobileQueryListener);
    this.onDestroySubject.next(true);
    this.onDestroySubject.unsubscribe();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  // ---- Toolbar filter / search handlers ----
  toggleFilter(filter: string): void {
    const current = [...this.selectedFilters.value];
    if (filter === 'ALL') {
      this.selectedFilters.next(['ALL']);
      return;
    }
    const withoutAll = current.filter(f => f !== 'ALL');
    const idx = withoutAll.indexOf(filter);
    if (idx > -1) {
      withoutAll.splice(idx, 1);
    } else {
      withoutAll.push(filter);
    }
    this.selectedFilters.next(withoutAll.length > 0 ? withoutAll : ['ALL']);
  }

  onSearchInputChange(event: Event): void {
    const query = (event.target as HTMLInputElement).value;
    this.searchQuery.next(query || '');
  }

  // ---- Expansion (via eye button) ----
  toggleExpand(row: OrderDeliverTo, event?: Event): void {
    if (event) { event.stopPropagation(); }
    this.expandedElement = this.expandedElement === row ? null : row;
  }

  // ---- Status categorization / row coloring ----

  /**
   * Return the PrintNodeOrders whose `orderdeliverto_ID` matches this
   * row. A single OrderDeliverTo can be fulfilled by more than one
   * PrintNodeOrder (split shipments) so we always work with the full
   * set. Returns `[]` when the print-node cache hasn't loaded yet.
   */
  private getPrintNodeOrdersFor(row: OrderDeliverTo): PrintNodeOrder[] {
    if (!row || !row.ID || !this.printNodeOrderService || !this.printNodeOrderService.objects) {
      return [];
    }
    const out: PrintNodeOrder[] = [];
    for (const pno of this.printNodeOrderService.objects) {
      if (pno && pno.orderdeliverto_ID === row.ID) { out.push(pno); }
    }
    return out;
  }

  /**
   * True when a Date value holds a plausibly-real ship/deliver time.
   * Rejects the model defaults (epoch = `new Date(0)`) AND any date
   * earlier than {@link MIN_REAL_SHIP_MS} — legacy rows carrying
   * near-epoch or year-0001 sentinel values would otherwise sneak
   * through as "shipped" and paint a nonsense date in the Shipped
   * column.
   */
  private isRealDate(d: any): boolean {
    if (!d) { return false; }
    const t = new Date(d).getTime();
    return !isNaN(t) && t > ShipmentOrdersComponent.MIN_REAL_SHIP_MS;
  }

  /**
   * True when at least one associated PrintNodeOrder has actually
   * shipped — either its `shippingStatus === 'SHIPPED'` or its
   * `shippedTimestamp` holds a real (post-epoch) date. The
   * OrderDeliverTo's own `shiptimestamp` alone is NOT trusted: it can
   * be populated on the row before any PrintNodeOrder has physically
   * shipped, which was showing a stale ship date in the UI.
   *
   * Returns false when no PrintNodeOrder has yet been linked to this
   * OrderDeliverTo (row is pre-fulfillment).
   */
  hasShipped(row: OrderDeliverTo): boolean {
    const pnos = this.getPrintNodeOrdersFor(row);
    for (const pno of pnos) {
      const ss = (pno.shippingStatus || '').toUpperCase();
      if (ss === 'SHIPPED' || ss === 'DELIVERED' || this.isRealDate(pno.shippedTimestamp)) {
        return true;
      }
    }
    return false;
  }

  /**
   * True when at least one associated PrintNodeOrder is marked as
   * delivered, per its `shippingStatus`. Requires `hasShipped(row)`
   * to also be true — nothing can be delivered without having
   * physically shipped first.
   */
  hasDelivered(row: OrderDeliverTo): boolean {
    if (!this.hasShipped(row)) { return false; }
    const pnos = this.getPrintNodeOrdersFor(row);
    for (const pno of pnos) {
      const ss = (pno.shippingStatus || '').toUpperCase();
      if (ss === 'DELIVERED') { return true; }
    }
    // Also honor the carrier-reported delivery on the row when a
    // PrintNodeOrder confirms it has shipped.
    return this.isRealDate(row.deliverytimestamp)
      || (row.carrierstatus || '').toUpperCase() === 'DELIVERED';
  }

  /**
   * Latest `shippedTimestamp` across the associated PrintNodeOrders,
   * or `null` if none have shipped. Used to populate the "Shipped"
   * line in the Created / Shipped column — deliberately sourced from
   * the PrintNodeOrder rather than `row.shiptimestamp` so a stale
   * OrderDeliverTo field can never paint a ship date for a row that
   * hasn't actually shipped.
   */
  getShippedTimestamp(row: OrderDeliverTo): Date | null {
    const pnos = this.getPrintNodeOrdersFor(row);
    let latest: number = 0;
    for (const pno of pnos) {
      if (this.isRealDate(pno.shippedTimestamp)) {
        const t = new Date(pno.shippedTimestamp).getTime();
        if (t > latest) { latest = t; }
      }
    }
    if (latest > 0) { return new Date(latest); }
    // Fall back to the row's own shiptimestamp ONLY when we also
    // confirmed via `hasShipped` that a PrintNodeOrder is actually
    // shipped — otherwise we'd re-introduce the stale-timestamp bug.
    if (this.hasShipped(row) && this.isRealDate(row.shiptimestamp)) {
      return new Date(row.shiptimestamp);
    }
    return null;
  }

  /**
   * Compact category used for both filtering and row background color.
   *
   * SHIPPED / DELIVERED are gated on `hasShipped(row)` (which itself
   * reads the associated PrintNodeOrders) so a stale `carrierstatus`
   * or a lingering `shiptimestamp` / `deliverytimestamp` on the row
   * doesn't paint an un-shipped OrderDeliverTo as SHIPPED / DELIVERED.
   * Physical rule: nothing can be delivered without first being
   * shipped, and nothing has shipped until at least one PrintNodeOrder
   * says so.
   */
  getShipmentCategory(row: OrderDeliverTo): string {
    if (!row) { return 'PENDING'; }
    const status = row.order ? (row.order.status || '').toUpperCase() : '';
    if (this.hasDelivered(row)) { return 'DELIVERED'; }
    if (this.hasShipped(row)) { return 'SHIPPED'; }
    if (status === 'HOLD' || (row.order && (row.order.shipmentUpdateStatus || '').toUpperCase() === 'HOLD')) {
      return 'ON_HOLD';
    }
    return 'PENDING';
  }

  hasAddressIssue(row: OrderDeliverTo): boolean {
    const v = ((row && row.addressValidationStatus) || '').toUpperCase();
    return v === 'ERROR' || v === 'BAD' || v === 'INVALID' || v === 'FAILED';
  }

  getRowBackgroundColor(row: OrderDeliverTo): string {
    if (this.hasAddressIssue(row)) { return '#f8c9c9'; }             // pinkish red — needs attention
    switch (this.getShipmentCategory(row)) {
      case 'DELIVERED': return '#d9d9d9';                             // grey — done
      case 'SHIPPED':   return '#c8e6c9';                             // green — in transit
      case 'ON_HOLD':   return '#ffe0b2';                             // orange — held
      case 'PENDING':
      default:          return '#fff9c4';                             // yellow — awaiting
    }
  }

  getTrackingLink(trackingNumber: string): string {
    return `https://www.google.com/search?q=${encodeURIComponent(trackingNumber)}`;
  }
}
